import React, {
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useReadEventApiV1EventsEventIdGet } from '@api-client/api-client';
import { Button } from '@components/ui/button';
import { PIN_REJECTED_TOAST } from '@components/community/spaceAccessCopy';
import { Drawer, DrawerContent, DrawerTitle } from '@components/ui/drawer';
import { toast } from '@hooks/use-toast';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useConfettiBurst } from '@hooks/useConfettiBurst';
import {
  useEventPinJoin,
  type EventPinJoinOutcome,
  type EventPinJoinResult,
  type EventPinJoinSurface,
} from '@hooks/useEventPinJoin';
import { useLatestRef } from '@hooks/useLatestRef';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';
import { useReturnFocusOnClose } from '@hooks/useReturnFocusOnClose';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { buildSingleSpacePayload } from '@lib/analytics/payloads';
import { findResolvedEventRead } from '@lib/cache/events';
import type { PinRequiredSpace, SelfReportedRegistration } from '@models/api';
import type { EventPinCelebration } from '@utils/eventPinCelebration';
import {
  EventPinCard,
  PRIMARY_BUTTON_CLASS,
  SheetBody,
  SheetFooter,
  TEXT_BUTTON_CLASS,
  type HostRowContent,
} from './EventPinCard';
import { EventPinTicketStep } from './EventPinTicketStep';
import { ENTRANCE_GUARD_MS } from './entranceGuard';
import { isDismissSwipe, rubberBandPx, swipeStartOf } from './eventPinSheetGestures';
import * as copy from './eventPinCopy';
import { useKeyboardSheetCap } from './useKeyboardSheetCap';
import {
  canRequest,
  canSubmitPin,
  requestIsPrimary,
  useEventPinSteps,
  type AnswerStep,
  type PinStep,
} from './useEventPinSteps';

/** How long an answer shows before the sheet moves on (D10: always, with a Continue button). */
const JOINED_MS = 1200;
const REQUESTED_MS = 1600;
/**
 * How a card was answered, for `event_space_pin_resolved`. `host_decided`: decided elsewhere while
 * the card was open, usually by a host (409 JOIN_ALREADY_MEMBER: approved, or joined from another
 * tab or surface; JOIN_REQUEST_NOT_PENDING), so neither the PIN nor a request from this card did.
 */
type ResolvedOutcome =
  'pin_joined' | 'pin_recorded_pending' | 'request_access' | 'host_decided' | 'dismissed';

/** The PIN funnel's space triplet, from the helper every join surface uses. */
const spaceTriplet = (space: PinRequiredSpace) =>
  buildSingleSpacePayload({ id: space.space_id, name: space.space_name, slug: space.space_slug });

/** Seconds from `start` (epoch ms) to now, to one decimal. Only called from handlers. */
const secondsSince = (start: number): number => Math.round((Date.now() - start) / 100) / 10;

// The line under the field is about the PIN only. Any other failure is said in a toast, by
// useEventPinJoin (`joinFailureToast`), and leaves the card as it was for another try.
const PIN_ERROR_COPY = { wrong: copy.wrongPin } as const;

const ANSWER_COPY = {
  pin_pending: copy.pinPending,
  requested: copy.requested,
} as const;

/**
 * The row under the host's name: the PIN entry on a card, the lockout once a 429 has locked it
 * (`lockedId`: the line Request access names as its description), the outcome once answered.
 */
function hostRow(step: PinStep | AnswerStep, spaceName: string, lockedId: string): HostRowContent {
  if (step.kind === 'pin') {
    if (step.rateLimited) return { kind: 'locked', text: copy.rateLimited, id: lockedId };
    return {
      kind: 'pin',
      submitting: step.submitting,
      error: step.error ? PIN_ERROR_COPY[step.error] : null,
      shakeKey: step.wrong,
      submitPrimary: !requestIsPrimary(step),
    };
  }
  // The joined and closed lines name the Space; the request lines are about the host.
  const text =
    step.outcome === 'joined'
      ? copy.joined(spaceName)
      : step.outcome === 'closed'
        ? copy.requestClosed(spaceName)
        : ANSWER_COPY[step.outcome];
  return { kind: 'answer', tick: step.outcome === 'joined', text };
}

export interface EventPinSheetHandle {
  /**
   * Dismiss the sheet as a gesture would. For the host's Android back (its grant's back handler):
   * on a PIN card it requests access and the sheet stays open; on an answer step with a step
   * after it, it moves on to that step (the next card, or the ticket question); on the last
   * answer and the ticket step it closes the sheet (`onClose`, plus `onTicketDismissed` on the
   * ticket step). Ignored while a PIN is being checked: its answer is on the way, and a request
   * now would race it. Ignored within ENTRANCE_GUARD_MS of the card or ticket step shown, like
   * every dismiss.
   */
  requestDismiss: () => void;
}

interface EventPinSheetProps {
  eventPin: EventPinCelebration;
  /** Arrival attribution of a post-auth Going, added to the ticket question's view event. */
  source: string | null;
  /** The host's ticket answer handler (CelebrationHost `answer`): it saves, closes, returns true. */
  onAnswer: (value: SelfReportedRegistration) => boolean;
  /** Close the sheet: a dismiss on the last answer or the ticket step, and the end of the flow. */
  onClose: () => void;
  /** The sheet was dismissed on the ticket step without an answer (for the host's analytics). */
  onTicketDismissed?: () => void;
  /** Exposes `requestDismiss` (see `EventPinSheetHandle`). */
  ref?: React.Ref<EventPinSheetHandle>;
}

/**
 * The one sheet after Going on an event whose host Space has a PIN (TECH-663): one PIN card per PIN
 * host, each followed by its answer, then the ticket question when the server asked for it. The
 * step machine is `useEventPinSteps`; this component owns the network, the timers and analytics.
 */
export const EventPinSheet: React.FC<EventPinSheetProps> = ({
  eventPin,
  source,
  onAnswer,
  onClose,
  onTicketDismissed,
  ref,
}) => {
  const { event_id: eventId, spaces, registration } = eventPin;
  const [state, dispatch] = useEventPinSteps(spaces, registration !== null);
  const { step } = state;
  const { trackEvent } = useAmplitudeTracking();
  const { submitPin, releaseHold } = useEventPinJoin(eventId);
  const reducedMotion = usePrefersReducedMotion();
  const queryClient = useQueryClient();
  const { data: eventResponse, isPending: eventPending } = useReadEventApiV1EventsEventIdGet(
    eventId,
    {
      // The Event page reads the Event by slug: seed from that read rather than ask again. The seed
      // keeps that read's age, and an invalidated read counts as stale, so either is re-read.
      initialData: () => findResolvedEventRead(queryClient, eventId)?.response,
      initialDataUpdatedAt: () => findResolvedEventRead(queryClient, eventId)?.dataUpdatedAt,
    },
  );
  const event = eventResponse?.data;

  // Read by the PIN response when it lands, to drop an answer the sheet no longer waits for.
  const stateRef = useLatestRef(state);
  const mountedRef = useRef(false);
  const cardShownAtRef = useRef(0);
  // When the card or ticket step on screen was shown (null until the first card): the entrance
  // guard's clock, re-armed by every new card and by the ticket step.
  const stepShownAtRef = useRef<number | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // This sheet's own scrim: the one outside tap that counts as a dismiss.
  const overlayRef = useRef<HTMLDivElement>(null);
  const requestButtonRef = useRef<HTMLButtonElement>(null);
  // While the keyboard is up, the sheet fits the visual viewport and its body scrolls.
  const keyboardMaxHeight = useKeyboardSheetCap(contentRef);
  // Focus goes back to the Going button on close. Read at the sheet's first render, before the PIN
  // input's autoFocus moves focus in during commit.
  const returnFocusOnClose = useReturnFocusOnClose();
  const joinSurface: EventPinJoinSurface = eventPin.reopen ? 'event_page_reopen' : 'event_going';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Confetti for the Going, on the first card and through its answer. Not on a sheet the Event page
  // reopened from a join hold: that Going was minutes ago, and the page is loading meanwhile.
  useConfettiBurst(
    !eventPin.reopen && (step.kind === 'pin' || step.kind === 'answer') && step.index === 0,
  );

  // Once per card shown: the PIN funnel's entry, and the clock of seconds_to_resolve.
  const shownIndex = step.kind === 'pin' ? step.index : null;
  const shownSpace = shownIndex === null ? null : spaces[shownIndex];
  const shownSpaceId = shownSpace ? shownSpace.space_id : null;
  const shownSpaceName = shownSpace ? shownSpace.space_name : null;
  const shownSpaceSlug = shownSpace ? shownSpace.space_slug : null;
  useEffect(() => {
    if (shownSpaceId === null) return;
    cardShownAtRef.current = Date.now();
    trackEvent(ANALYTICS_EVENTS.pin_drawer_opened, {
      ...buildSingleSpacePayload({ id: shownSpaceId, name: shownSpaceName, slug: shownSpaceSlug }),
      join_surface: joinSurface,
      event_id: eventId,
    });
  }, [shownSpaceId, shownSpaceName, shownSpaceSlug, joinSurface, eventId, trackEvent]);

  // A 429 locks the card: the PIN field that had focus is gone, so focus moves to Request access,
  // now the one way on. The lockout line is its description, so the reason is read with the move.
  const locked = step.kind === 'pin' && step.rateLimited;
  const lockedId = useId();
  useEffect(() => {
    if (locked) requestButtonRef.current?.focus();
  }, [locked]);

  // Re-arms the entrance guard on every newly shown card and on the ticket step. `entering` is
  // the guard as a render value, for vaul: a dismissible step (the ticket step) stays
  // undismissible to vaul until its guard has run out, because a drag vaul releases calls
  // closeDrawer(), which `inEntrance()` can refuse but cannot undo (the sheet would stay where the
  // finger let go, with the scrim up).
  const guardedStepKey =
    step.kind === 'pin' ? `pin:${step.index}` : step.kind === 'ticket' ? 'ticket' : null;
  const [settledStepKey, setSettledStepKey] = useState<string | null>(null);
  // The clock is set in a layout effect, as the step commits. A step changed by a timer (an
  // answer's) runs its passive effects in a later scheduler task, and a tap that lands before it
  // would still read the previous card's clock, long past.
  useLayoutEffect(() => {
    if (guardedStepKey !== null) stepShownAtRef.current = Date.now();
  }, [guardedStepKey]);
  useEffect(() => {
    if (guardedStepKey === null) return;
    const timer = setTimeout(() => {
      setSettledStepKey(guardedStepKey);
    }, ENTRANCE_GUARD_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [guardedStepKey]);
  const entering = guardedStepKey !== null && settledStepKey !== guardedStepKey;

  const isLastAnswer =
    step.kind === 'answer' && step.index + 1 >= spaces.length && registration === null;
  const advance = () => {
    dispatch({ type: 'advance' });
    // The last card with no ticket question ends the flow.
    if (isLastAnswer) onClose();
  };
  const advanceRef = useLatestRef(advance);

  // The answer shows for a moment, then the sheet moves on.
  const answerKey = step.kind === 'answer' ? `${step.index}:${step.outcome}` : null;
  const answerMs =
    step.kind === 'answer' && (step.outcome === 'requested' || step.outcome === 'closed')
      ? REQUESTED_MS
      : JOINED_MS;
  useEffect(() => {
    if (answerKey === null) return;
    const timer = setTimeout(() => {
      advanceRef.current();
    }, answerMs);
    return () => {
      clearTimeout(timer);
    };
  }, [answerKey, answerMs, advanceRef]);

  // Once the card's `release_at` has passed, the server's timer has released the hold and
  // reported this card itself (`event_space_pin_resolved`, outcome `timeout`): a second
  // resolution from here would count the card twice. An unparsable time is not a passed one.
  const trackResolved = (outcome: ResolvedOutcome, card: PinStep, spaceId: string) => {
    const releaseAt = Date.parse(spaces.find(s => s.space_id === spaceId)?.release_at ?? '');
    if (Date.now() >= releaseAt) return;
    trackEvent(ANALYTICS_EVENTS.event_space_pin_resolved, {
      outcome,
      wrong_attempts: card.wrong,
      seconds_to_resolve: secondsSince(cardShownAtRef.current),
      event_id: eventId,
      space_id: spaceId,
      join_surface: joinSurface,
    });
  };

  // Entrance guard: the second tap of the tap that showed a step must not land on that step's
  // irreversible actions: a request for access, which tells the host at once, or a ticket answer.
  // Within ENTRANCE_GUARD_MS of a card or the ticket step being shown, every dismiss, the Request
  // access button and the ticket answers do nothing. Only called from handlers.
  const inEntrance = () => {
    const shownAt = stepShownAtRef.current;
    return shownAt === null || Date.now() - shownAt < ENTRANCE_GUARD_MS;
  };

  /**
   * Request access on the card shown: tell the host now, and show "Request sent". Refused while a
   * PIN is checked (`canRequest`): its answer is on the way. `via` only picks the reported outcome.
   */
  const request = (via: 'button' | 'dismiss') => {
    if (!canRequest(step) || inEntrance()) return;
    const spaceId = spaces[step.index].space_id;
    releaseHold(spaceId);
    trackResolved(via === 'button' ? 'request_access' : 'dismissed', step, spaceId);
    dispatch({ type: 'requested' });
  };

  const applyOutcome = (outcome: EventPinJoinOutcome, card: PinStep, spaceId: string) => {
    switch (outcome) {
      case 'joined':
        trackResolved('pin_joined', card, spaceId);
        dispatch({ type: 'joined' });
        return;
      case 'already_member':
        // In meanwhile (a host approved, or a join from another tab): not through this PIN.
        trackResolved('host_decided', card, spaceId);
        dispatch({ type: 'joined' });
        return;
      case 'pin_pending':
        trackResolved('pin_recorded_pending', card, spaceId);
        dispatch({ type: 'pin_pending' });
        return;
      case 'unlisted':
        // The PIN cannot open an unlisted Space: the request it already is goes to the host now.
        releaseHold(spaceId);
        trackResolved('request_access', card, spaceId);
        dispatch({ type: 'requested' });
        return;
      case 'pin_off':
        // The PIN was turned off while the card was open: the request goes to the host now.
        releaseHold(spaceId);
        trackResolved('request_access', card, spaceId);
        dispatch({ type: 'requested' });
        return;
      case 'decided':
        // A host decided the request meanwhile (or it is gone): a release would send nothing,
        // and there is no request left to say "Request sent" about. The card says so, in the
        // words every join surface uses for this conflict, and moves on with no chip.
        trackResolved('host_decided', card, spaceId);
        dispatch({ type: 'closed' });
        return;
      case 'wrong_pin':
      case 'rate_limited':
      case 'error':
        dispatch({ type: outcome });
    }
  };

  const submit = (pin: string) => {
    if (!canSubmitPin(step)) return;
    const { index } = step;
    const space = spaces[index];
    const spaceId = space.space_id;
    // Before the call, as the Space page's PIN drawer does: the gesture lands whatever the answer.
    trackEvent(ANALYTICS_EVENTS.pin_drawer_submitted, {
      ...spaceTriplet(space),
      join_surface: joinSurface,
      event_id: eventId,
    });
    dispatch({ type: 'submit' });
    void submitPin(space, pin, joinSurface)
      .catch((error: unknown): EventPinJoinResult => ({ outcome: 'error', error }))
      .then(({ outcome }) => {
        // Stale: the sheet closed, or moved past this card, while the PIN was checked. A join
        // still refreshed the caches and was still reported, and a failure was still said
        // (useEventPinJoin's onSuccess).
        // A wrong PIN is said in a toast instead of the line under a field nobody sees, as on
        // every PIN surface; nothing else is left to show.
        const current = stateRef.current.step;
        if (!mountedRef.current || current.kind !== 'pin' || current.index !== index) {
          if (outcome === 'wrong_pin') toast(PIN_REJECTED_TOAST);
          return;
        }
        applyOutcome(outcome, current, spaceId);
      });
  };

  const dismiss = () => {
    if (inEntrance()) return;
    if (step.kind === 'pin') {
      request('dismiss');
      return;
    }
    // An answer moves on, as its timer would: a stray swipe never skips the next host's card or
    // the ticket question. On the last answer, moving on is the end of the flow (`advance` closes).
    if (step.kind === 'answer') {
      advance();
      return;
    }
    if (step.kind === 'ticket') onTicketDismissed?.();
    if (step.kind !== 'done') onClose();
  };

  // Lets go of a pull on a held card: vaul's own `transition: transform .5s` springs the sheet
  // home. Only after a pull that started on a held step (`swipeStartRef` is set only there): on a
  // dismissible step the inline transform is vaul's own drag, which its onRelease reads after this
  // handler to decide whether to close.
  const releasePull = () => {
    const sheet = contentRef.current;
    if (swipeStartRef.current === null || !sheet) return;
    sheet.style.transition = '';
    sheet.style.transform = '';
  };

  // A pull belongs to the step it began on. When the step changes under the finger (an answer's
  // timer, a PIN answer), the pull ends there: the sheet springs home and the release dismisses
  // nothing, so a slow pull on "Request sent" can never request access for the next host's card.
  // A layout effect, so a parked rubber band is gone before the next step paints.
  const pullStepKey =
    step.kind === 'pin' || step.kind === 'answer' ? `${step.kind}:${step.index}` : step.kind;
  useLayoutEffect(() => {
    const sheet = contentRef.current;
    const swipeStart = swipeStartRef;
    return () => {
      if (swipeStart.current !== null && sheet) {
        sheet.style.transition = '';
        sheet.style.transform = '';
      }
      swipeStart.current = null;
    };
  }, [pullStepKey]);

  // The ticket answers are irreversible too (saved, and "I need a ticket" opens the ticket site):
  // refused within the entrance guard, so nothing is saved or opened.
  const answerTicket = (value: SelfReportedRegistration): boolean =>
    inEntrance() ? false : onAnswer(value);

  useImperativeHandle(ref, () => ({ requestDismiss: dismiss }));

  // The steps a dismiss does not close: a PIN card (it requests access) and an answer with a step
  // after it (it moves on). vaul cannot keep a sheet open after a drag release (see below).
  const holdsOpen = step.kind === 'pin' || (step.kind === 'answer' && !isLastAnswer);
  const motionClass = reducedMotion ? 'animate-pin-sheet-fade' : 'animate-pin-sheet-slide';
  // The card's event block: placeholders while the Event is first read (off the Event page, whose
  // slug read seeds it), nothing if it cannot be read.
  const cardEvent = event ?? (eventPending ? 'loading' : null);

  // `done` shows for the moment before the host unmounts the sheet; the title keeps the dialog named.
  let content: React.ReactNode = <DrawerTitle className="sr-only">{copy.goingTitle}</DrawerTitle>;
  if (step.kind === 'ticket' && registration) {
    content = (
      <EventPinTicketStep
        registration={registration}
        source={source}
        onAnswer={answerTicket}
        chip={step.chip}
        motionClass={motionClass}
      />
    );
  } else if (step.kind === 'pin' || step.kind === 'answer') {
    const space = spaces[step.index];
    content = (
      <>
        <SheetBody
          animationKey={`card-${step.index}`}
          motionClass={step.index === 0 ? undefined : motionClass}
        >
          <EventPinCard
            space={space}
            event={cardEvent}
            chip={step.kind === 'pin' ? step.chip : null}
            row={hostRow(step, space.space_name, lockedId)}
            onSubmitPin={submit}
          />
        </SheetBody>
        <SheetFooter>
          {step.kind === 'pin' ? (
            <Button
              ref={requestButtonRef}
              type="button"
              className={requestIsPrimary(step) ? PRIMARY_BUTTON_CLASS : TEXT_BUTTON_CLASS}
              aria-describedby={locked ? lockedId : undefined}
              disabled={!canRequest(step)}
              // A held Enter that began elsewhere (the PIN field, before a 429 moved focus here)
              // never activates it: each OS repeat would be a request the member never made.
              onKeyDown={e => {
                if (e.key === 'Enter' && e.repeat) e.preventDefault();
              }}
              onClick={() => {
                request('button');
              }}
            >
              {copy.requestAccess}
            </Button>
          ) : (
            // D10: screen-reader users get a way past the timer; hidden until focused.
            <Button
              type="button"
              className={`sr-only focus:not-sr-only ${TEXT_BUTTON_CLASS}`}
              onClick={advance}
            >
              {copy.continueLabel}
            </Button>
          )}
        </SheetFooter>
      </>
    );
  }

  return (
    // On a PIN card every dismiss (overlay tap, Escape, swipe, Android back) is a request for
    // access and the sheet stays open; on an answer with a step after it, a dismiss moves on.
    // Controlled `open` alone cannot do that in vaul 1.1.2: a drag released past the threshold
    // calls `closeDrawer()`, which reports onOpenChange(false) but leaves the sheet, scrim and
    // scaled background where the finger let go (only a release below the threshold runs
    // `resetDrawer()`), and runs its close side effects. So those steps are
    // `dismissible={false}`: vaul then starts no drag and drops Radix's dismiss before
    // onOpenChange. A tap on this sheet's own scrim (`overlayRef`: every layer's scrim shares one
    // portal container) and Escape are caught on the content instead, and a swipe down by the
    // pointer handlers (`swipeStartOf` / `isDismissSwipe`: the sheet gives a little under the
    // finger, `rubberBandPx`, and settles back on release, so the pull reads as registered but
    // never as closing; a pan inside the scrolling body is the browser's and cancels the pointer,
    // as it does vaul's own drag).
    // The last answer and the ticket step are dismissible as usual, and any dismiss there closes;
    // the ticket step only once its entrance guard has run out (`entering`), so vaul starts no
    // drag before then.
    // repositionInputs={false}: vaul does not lift the sheet over the keyboard; useKeyboardSheetCap
    // fits it to the visual viewport instead.
    <Drawer
      open
      repositionInputs={false}
      dismissible={!holdsOpen && !entering}
      onOpenChange={open => {
        if (!open) dismiss();
      }}
    >
      <DrawerContent
        ref={contentRef}
        overlayRef={overlayRef}
        hideHandle
        className="h-[76dvh]"
        style={keyboardMaxHeight === null ? undefined : { maxHeight: keyboardMaxHeight }}
        onEscapeKeyDown={e => {
          // Only a fresh Escape is a dismiss: a held one would skip the answer it gave and then
          // request access on the next host's card. preventDefault also stops Radix and vaul
          // closing a dismissible step on a repeat.
          if (e.repeat) {
            e.preventDefault();
            return;
          }
          if (holdsOpen) dismiss();
        }}
        onCloseAutoFocus={returnFocusOnClose}
        onPointerDownOutside={e => {
          // DrawerContent prevented it: a tap on a toast, never a request for access.
          if (e.defaultPrevented) return;
          const target = e.detail.originalEvent.target;
          if (holdsOpen && target instanceof Node && overlayRef.current?.contains(target)) {
            dismiss();
          }
        }}
        onPointerDown={e => {
          // A pull whose release never arrived (a mouse let go outside the window) lets go now.
          releasePull();
          swipeStartRef.current = holdsOpen ? swipeStartOf(e) : null;
        }}
        onPointerMove={e => {
          const start = swipeStartRef.current;
          const sheet = contentRef.current;
          if (!holdsOpen || start === null || !sheet) return;
          // The sheet gives under the finger (rubber band) and never travels. Inline transform on
          // the sheet itself: composited, no React render and no layout per frame.
          sheet.style.transition = 'none';
          sheet.style.transform = `translate3d(0, ${rubberBandPx(Math.max(0, e.clientY - start.y))}px, 0)`;
        }}
        onPointerCancel={() => {
          releasePull();
          swipeStartRef.current = null;
        }}
        onPointerUp={e => {
          const start = swipeStartRef.current;
          releasePull();
          swipeStartRef.current = null;
          if (holdsOpen && start !== null && isDismissSwipe(start, e)) dismiss();
        }}
      >
        {/* The handle, with a hit area tall enough to grab: the one place a mouse drag dismisses. */}
        <div
          data-pin-sheet-handle=""
          aria-hidden="true"
          className="flex h-9 shrink-0 justify-center pt-4"
        >
          <div className="h-2 w-25 rounded-full bg-muted" />
        </div>
        {content}
      </DrawerContent>
    </Drawer>
  );
};
